import React, { Component } from 'react'
import Swal from 'sweetalert2'
import { AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle, AiOutlineInfoCircle, AiOutlineReload, AiOutlineArrowLeft, AiOutlineSearch } from "react-icons/ai"
import { getInformationWithDataPrefix, getInformationWithDataV3 } from '../../services/CABE'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }
const BTN_DISABLED = { ...BTN_PRIMARY, background: '#94A3B8', cursor: 'not-allowed' }
const INPUT_STYLE = { border: '1px solid #CBD5E1', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.9rem', width: '100%', background: '#FFFFFF', color: '#0F172A' }
const LABEL_STYLE = { display: 'block', fontSize: '0.78rem', color: '#475569', marginBottom: '0.25rem', fontWeight: 600 }
const NOTICE_STYLE = { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.75rem 0.9rem', borderRadius: '8px', fontSize: '0.85rem', marginTop: '0.75rem' }
const NOTICE_INFO = { ...NOTICE_STYLE, background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E3A8A' }
const NOTICE_WARN = { ...NOTICE_STYLE, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }
const NOTICE_ERROR = { ...NOTICE_STYLE, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }
const NOTICE_OK = { ...NOTICE_STYLE, background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }
const CARD_STYLE = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '1rem' }

// Altitude only — every call is pinned to company 6 / the /alt prefix,
// regardless of the session's company cookie.
const ID_COMPANY = 6
const API_PREFIX = '/alt'

// SalesOrderHeader.OrderStatus in the portal; no row (order created straight
// in Sage) is shown as "Not started", same as SalesOrderPicking.stateInWords().
const STATUS = {
    0: { label: 'Not started', bg: '#F1F5F9', fg: '#334155', border: '#CBD5E1' },
    1: { label: 'In progress', bg: '#FFFBEB', fg: '#92400E', border: '#FDE68A' },
    2: { label: 'Finished', bg: '#F0FDF4', fg: '#166534', border: '#BBF7D0' },
}
const STATUS_FILTERS = ['all', 0, 1, 2]

const LIST_MAX_SHOWN = 100
const SEARCH_MIN_CHARS = 2
const SEARCH_MAX_CHARS = 50
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MAX_SHOWN = 25
// The update only queues the message for Sage — "ok" doesn't mean it was
// applied, so the change is confirmed by reading the SO back.
const VERIFY_DELAYS_MS = [3000, 5000]

const STEPS = ['Choose customer', 'Send to Sage', 'Confirm in Sage', 'Update portal']
const BUSY_PHASES = ['loading', 'sending', 'verifying', 'applying']

const errorMessageOf = (response) => {
    if (response && response.message) return response.message
    if (response && response.error) return response.error
    return ''
}

const isOk = (response) => !!(response && response.status && response.status.code === 1)

const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

const customerLabel = (c) => c ? `${c.customerName} (${c.customerNo})` : ''

const formatDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
    return match ? `${match[2]}/${match[3]}/${match[1]}` : (value || '—')
}

const formatMoney = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'
}

const normalizeStatus = (value) => (value === 1 || value === 2 ? value : 0)

const ORDER_STATE = {
    order: null,          // { salesOrderNo, orderDate, customerNo, customerName, salespersonNo }
    currentLevel: null,   // customer_price_level of the SO's current customer (default 1)
    portalRecord: null,   // last customer the portal recorded for this SO, or null
    loadError: '',
    search: '',
    searching: false,
    searchError: '',
    searchOpen: false,
    results: [],          // [{ customerNo, customerName, level, isCurrent, disabled }]
    activeIndex: -1,
    selected: null,
    verifyAttempt: 0,
    flowError: '',
    summary: null,        // { mode, from, to, finishedAt }
}

/**
 * Sales → Customer Correction (Altitude) — 2026-10-14.
 *
 * Same flow as Tools → Customer Correction (CustomerCorrectionTool.js), but
 * for a manager fixing an Altitude order a salesperson created with the
 * wrong customer:
 *   1. Lists every open Altitude SO in Sage (all salespeople) that has no
 *      invoice yet — Not started, In progress and Finished — with search and
 *      status/salesperson filters.
 *   2. Picking one re-checks it live (Sage header + audit trail) and offers
 *      only customers on the SAME portal price level, so prices stay valid.
 *   3. Sends UpdateSalesOrderCustomer to Sage, verifies it by reading the SO
 *      back and then records it in the portal (/v3/audit/correct-customer).
 *      The MAS90 worker also updates the Mayaland mirror's comment.
 * Access is controlled by module 65 in the menu; the backend allows admin
 * and manager users on the portal update.
 */
export class AltitudeCustomerCorrection extends Component {
    constructor(props) {
        super(props)
        this.state = {
            orders: [],
            loadingOrders: false,
            ordersError: '',
            filterText: '',
            filterStatus: 'all',
            filterSalesperson: 'all',
            phase: 'list',   // list | loading | loaded | sending | verifying | verifyFailed | applying | applyFailed | done
            ...ORDER_STATE,
        }
        this._timers = []
        this._searchTimer = null
        this._searchSeq = 0
        this._ordersSeq = 0
        this._unloadGuard = false
        this._unmounted = false
    }

    componentDidMount() {
        this.loadOrders()
    }

    componentWillUnmount() {
        this._unmounted = true
        this._timers.forEach(clearTimeout)
        clearTimeout(this._searchTimer)
        this.setUnloadGuard(false)
    }

    wait = (ms) => new Promise((resolve) => { this._timers.push(setTimeout(resolve, ms)) })

    handleBeforeUnload = (e) => {
        e.preventDefault()
        e.returnValue = ''
    }

    setUnloadGuard(on) {
        if (on && !this._unloadGuard) window.addEventListener('beforeunload', this.handleBeforeUnload)
        if (!on && this._unloadGuard) window.removeEventListener('beforeunload', this.handleBeforeUnload)
        this._unloadGuard = on
    }

    // ── Data access ──────────────────────────────────────────────────────

    async fetchSageHeader(docNo) {
        const resp = await getInformationWithDataPrefix('/sales/get/header/by-no', { SalesOrderNo: docNo, idCompany: ID_COMPANY }, API_PREFIX)
        if (!isOk(resp)) return { error: true, row: null }
        const rows = Array.isArray(resp.data) ? resp.data : []
        // Match on SalesOrderNo too, not just data[0] — the WS bridge has
        // returned responses for the wrong request before.
        return { error: false, row: rows.find((r) => String(r.SalesOrderNo).toUpperCase() === docNo) || null }
    }

    async fetchInvoiceLink(docNo) {
        const resp = await getInformationWithDataV3('/v3/audit/trail', { docNo, idCompany: ID_COMPANY })
        if (!isOk(resp)) return { error: true }
        const events = Array.isArray(resp.data) ? resp.data : []
        const invoiced = events.find((e) => e.action === 'converted_to_invoice' && String(e.linkedSoNo).toUpperCase() === docNo)
        return { error: false, invoiceNo: invoiced ? invoiced.docNo : null }
    }

    async fetchPortalRecord(docNo) {
        const resp = await getInformationWithDataV3('/v3/audit/original-customer', { docNo, idCompany: ID_COMPANY })
        const data = isOk(resp) ? resp.data : null
        return data && data.found ? { customerNo: String(data.customerNo), customerName: data.customerName || '' } : null
    }

    async searchCustomers(search) {
        const resp = await getInformationWithDataV3('/v3/pricing/customer-level/list', { idCompany: ID_COMPANY, search, page: 1, pageSize: 50 })
        if (!isOk(resp)) return null
        return resp.data && Array.isArray(resp.data.customers) ? resp.data.customers : []
    }

    // Same default as the pricing backend: no customer_price_level row → level 1.
    async fetchPortalLevel(customerNo) {
        const customers = await this.searchCustomers(customerNo)
        if (customers === null) return null
        const match = customers.find((c) => String(c.customerNo) === customerNo)
        return match ? Number(match.priceLevel) || 1 : 1
    }

    // ── Order list ───────────────────────────────────────────────────────

    // Open Altitude SOs from Sage (SalespersonNo '0000' = every salesperson)
    // minus the ones that already have an invoice in Sage, same rule as
    // SalesOrderPicking.getActualOrders().
    loadOrders = async () => {
        const seq = ++this._ordersSeq
        this.setState({ loadingOrders: true, ordersError: '' })
        const query = { idCompany: ID_COMPANY, idcompany: ID_COMPANY, SalespersonNo: '0000' }
        // One after the other, never in parallel: both go over the same Altitude
        // WS bridge, which has handed one request's rows to the other before.
        const headers = await getInformationWithDataPrefix('/sales/get/header', query, API_PREFIX)
        if (this._unmounted || seq !== this._ordersSeq) return
        const invoices = await getInformationWithDataPrefix('/invoice/header', { ...query, CustomerNo: '*' }, API_PREFIX)
        if (this._unmounted || seq !== this._ordersSeq) return

        const headerRows = Array.isArray(headers && headers.data) ? headers.data : []
        const invoiceRows = Array.isArray(invoices && invoices.data) ? invoices.data : []
        // Without the invoice list we can't tell which orders are safe to change.
        // SO header rows never carry an InvoiceNo — if they do, the responses got crossed.
        if (!isOk(headers) || !isOk(invoices) || headerRows.some((r) => r.InvoiceNo)) {
            this.setState({ loadingOrders: false, orders: [], ordersError: 'Could not load the orders from Sage. Please try again.' })
            return
        }

        const invoiced = new Set(invoiceRows.map((i) => String(i.SalesOrderNo || '').toUpperCase()).filter(Boolean))
        const orders = headerRows
            .filter((h) => h.SalesOrderNo && h.CustomerNo && !invoiced.has(String(h.SalesOrderNo).toUpperCase()))
            .map((h) => ({
                salesOrderNo: String(h.SalesOrderNo).toUpperCase(),
                orderDate: h.OrderDate || '',
                customerNo: String(h.CustomerNo),
                customerName: h.BillToName || '',
                salespersonNo: h.SalespersonNo || '',
                total: h.NonTaxableAmt,
                status: normalizeStatus(h.Status),
            }))
            // Sage returns oldest first — newest first is what a manager looks for.
            .reverse()

        this.setState({ loadingOrders: false, orders })
    }

    filteredOrders() {
        const { orders, filterText, filterStatus, filterSalesperson } = this.state
        const term = filterText.trim().toLowerCase()
        return orders.filter((o) =>
            (filterStatus === 'all' || o.status === filterStatus) &&
            (filterSalesperson === 'all' || o.salespersonNo === filterSalesperson) &&
            (!term || o.salesOrderNo.toLowerCase().includes(term) || o.customerName.toLowerCase().includes(term) ||
                o.customerNo.includes(term) || o.salespersonNo.toLowerCase().includes(term)))
    }

    // ── Step 1: open an order (re-checked live) ──────────────────────────

    handleOpenOrder = async (listed) => {
        const docNo = listed.salesOrderNo
        clearTimeout(this._searchTimer)
        this._searchSeq++
        this.setState({ ...ORDER_STATE, phase: 'loading', order: listed })
        window.scrollTo(0, 0)

        const [header, invoice, portalRecord] = await Promise.all([
            this.fetchSageHeader(docNo),
            this.fetchInvoiceLink(docNo),
            this.fetchPortalRecord(docNo),
        ])
        if (this._unmounted) return

        if (header.error) {
            this.setState({ phase: 'loaded', order: null, loadError: 'Could not reach Sage. Go back and try again.' })
            return
        }
        if (!header.row || !header.row.CustomerNo) {
            this.setState({ phase: 'loaded', order: null, loadError: `${docNo} is no longer open in Sage — it was invoiced or closed after the list was loaded.` })
            return
        }
        if (invoice.error) {
            this.setState({ phase: 'loaded', order: null, loadError: `Could not confirm whether ${docNo} was already invoiced. Go back and try again.` })
            return
        }
        if (invoice.invoiceNo) {
            this.setState({ phase: 'loaded', order: null, loadError: `${docNo} was already invoiced (${invoice.invoiceNo}). Its customer can't be changed.` })
            return
        }

        const order = {
            salesOrderNo: docNo,
            orderDate: header.row.OrderDate || listed.orderDate,
            customerNo: String(header.row.CustomerNo),
            customerName: header.row.BillToName || '',
            salespersonNo: header.row.SalespersonNo || listed.salespersonNo,
            total: listed.total,
            status: listed.status,
        }
        const currentLevel = await this.fetchPortalLevel(order.customerNo)
        if (this._unmounted) return
        if (currentLevel === null) {
            this.setState({ phase: 'loaded', order: null, loadError: "Could not load the current customer's price level. Go back and try again." })
            return
        }

        this.setState({ phase: 'loaded', order, currentLevel, portalRecord })
    }

    handleBackToList = (reload) => {
        clearTimeout(this._searchTimer)
        this._searchSeq++
        this.setState({ ...ORDER_STATE, phase: 'list' })
        if (reload) this.loadOrders()
    }

    // ── Step 2: search / select ──────────────────────────────────────────

    handleSearchChange = (e) => {
        const search = e.target.value.slice(0, SEARCH_MAX_CHARS)
        clearTimeout(this._searchTimer)
        this.setState({ search, selected: null, searchError: '' })

        const term = search.trim()
        if (term.length < SEARCH_MIN_CHARS) {
            this._searchSeq++
            this.setState({ results: [], searching: false, searchOpen: false, activeIndex: -1 })
            return
        }
        this._searchTimer = setTimeout(() => this.runSearch(term), SEARCH_DEBOUNCE_MS)
    }

    runSearch = async (term) => {
        const seq = ++this._searchSeq
        this.setState({ searching: true })
        const customers = await this.searchCustomers(term)
        // Drop responses for an older search term.
        if (this._unmounted || seq !== this._searchSeq) return

        if (customers === null) {
            this.setState({ searching: false, results: [], searchOpen: false, searchError: 'Could not search customers. Please try again.' })
            return
        }

        const { order, currentLevel } = this.state
        const results = customers
            .map((c) => {
                const customerNo = String(c.customerNo)
                const level = Number(c.priceLevel) || 1
                const isCurrent = customerNo === order.customerNo
                return { customerNo, customerName: c.customerName || '', level, isCurrent, disabled: isCurrent || level !== currentLevel }
            })
            .sort((a, b) => (a.disabled - b.disabled) || a.customerName.localeCompare(b.customerName))

        const visible = results.slice(0, SEARCH_MAX_SHOWN)
        this.setState({ searching: false, results, searchOpen: true, activeIndex: visible.findIndex((r) => !r.disabled) })
    }

    handleSearchKeyDown = (e) => {
        const { results, activeIndex, searchOpen } = this.state
        const visible = results.slice(0, SEARCH_MAX_SHOWN)

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            if (!searchOpen) {
                if (visible.length) this.setState({ searchOpen: true })
                return
            }
            const step = e.key === 'ArrowDown' ? 1 : -1
            let i = activeIndex < 0 ? (step > 0 ? -1 : 0) : activeIndex
            for (let n = 0; n < visible.length; n++) {
                i = (i + step + visible.length) % visible.length
                if (!visible[i].disabled) {
                    this.setState({ activeIndex: i })
                    break
                }
            }
        } else if (e.key === 'Enter') {
            const option = visible[activeIndex]
            if (searchOpen && option && !option.disabled) {
                e.preventDefault()
                this.selectCustomer(option)
            }
        } else if (e.key === 'Escape') {
            this.setState({ searchOpen: false })
        }
    }

    selectCustomer(customer) {
        if (customer.disabled) return
        this.setState({ selected: customer, search: `${customer.customerNo} — ${customer.customerName}`, searchOpen: false })
    }

    // ── Steps 3–4: send, verify, update portal ───────────────────────────

    handleSend = async () => {
        const { order, selected, currentLevel } = this.state
        if (!order || !selected || selected.disabled) return

        const confirmation = await Swal.fire({
            title: 'Change the customer?',
            html: `
                <div style="text-align:left;font-size:0.9rem;line-height:1.6">
                    <div><strong>Sales Order:</strong> ${escapeHtml(order.salesOrderNo)} · Altitude</div>
                    <div><strong>From:</strong> ${escapeHtml(customerLabel(order))}</div>
                    <div><strong>To:</strong> ${escapeHtml(customerLabel(selected))}</div>
                    <div><strong>Price level:</strong> ${escapeHtml(currentLevel)} (prices don't change)</div>
                    <div style="margin-top:0.5rem;color:#475569">Sage and the Mayaland copy of this order will be updated.</div>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            focusCancel: true,
            confirmButtonText: 'Yes, change it',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#2563EB',
            cancelButtonColor: '#64748B',
        })
        if (!confirmation.isConfirmed || this._unmounted) return

        this.setUnloadGuard(true)
        this.setState({ phase: 'sending', flowError: '' })

        // Same channel as CustomerCorrectionTool: /Test/EnvioOrden always goes
        // through apiMAS90 and the worker picks the company by IdCompany and
        // the action by OrderType (see the TEMPORAL note there).
        const resp = await getInformationWithDataPrefix('/Test/EnvioOrden', {
            SalesOrderNo: order.salesOrderNo,
            CustomerNo: selected.customerNo,
            IdCompany: ID_COMPANY,
            OrderType: 'UpdateSalesOrderCustomer',
        }, '')
        if (this._unmounted) return

        // A 4xx is a definite rejection — nothing reached Sage. A timeout,
        // network error or 5xx is ambiguous (it may have been queued), so
        // those still go through verification instead of inviting a resend.
        if (!isOk(resp) && resp && resp.httpStatus >= 400 && resp.httpStatus < 500) {
            this.setUnloadGuard(false)
            this.setState({ phase: 'loaded', flowError: `Sage rejected the request${errorMessageOf(resp) ? `: ${errorMessageOf(resp)}` : ''}. Nothing was changed — you can try again.` })
            return
        }

        this.setState({ phase: 'verifying', verifyAttempt: 1 })
        let lastSeen = ''
        for (let i = 0; i < VERIFY_DELAYS_MS.length; i++) {
            this.setState({ verifyAttempt: i + 1 })
            await this.wait(VERIFY_DELAYS_MS[i])
            if (this._unmounted) return

            const { row, error } = await this.fetchSageHeader(order.salesOrderNo)
            if (this._unmounted) return

            if (row && String(row.CustomerNo) === selected.customerNo) {
                await this.applyPortal({ mode: 'sage', from: order, to: selected })
                return
            }
            lastSeen = error ? 'no response from Sage' : row ? `still ${row.BillToName} (${row.CustomerNo})` : 'order not found'
        }

        this.setUnloadGuard(false)
        this.setState({
            phase: 'verifyFailed',
            flowError: `Sage doesn't show the new customer yet (${lastSeen}). The change may still be applied — reload the order and check the customer BEFORE sending it again.`,
        })
    }

    // The portal correction (APPEND to document_audit_trail + sync of
    // sales_orders_aws_v2). It re-reads the customer from Sage itself.
    applyPortal = async (summary) => {
        const { order } = this.state
        this.setUnloadGuard(true)
        this.setState({ phase: 'applying', flowError: '', summary })

        const reason = summary.mode === 'sage'
            ? 'Customer changed in Sage from Sales: Customer Correction (Altitude)'
            : 'Corregido desde Sales: Customer Correction (Altitude)'
        const resp = await getInformationWithDataV3('/v3/audit/correct-customer', { docNo: order.salesOrderNo, idCompany: ID_COMPANY, reason })
        if (this._unmounted) return
        this.setUnloadGuard(false)

        if (isOk(resp) && resp.data) {
            // changed:false means the portal already had this customer
            // recorded — still a successful end state.
            const to = { customerNo: String(resp.data.customerNo), customerName: resp.data.customerName || summary.to.customerName }
            this.setState({
                phase: 'done',
                order: { ...order, ...to },
                summary: { ...summary, to, finishedAt: new Date().toLocaleString() },
            })
        } else {
            this.setState({ phase: 'applyFailed', flowError: errorMessageOf(resp) || 'The portal could not be updated.' })
        }
    }

    handleSyncPortalOnly = () => {
        const { order, portalRecord } = this.state
        this.applyPortal({ mode: 'portal', from: portalRecord, to: order })
    }

    handleRetryPortal = () => this.applyPortal(this.state.summary)

    handleReloadOrder = () => this.handleOpenOrder(this.state.order)

    // ── Render: shared bits ──────────────────────────────────────────────

    renderStatusBadge(status) {
        const s = STATUS[status] || STATUS[0]
        return (
            <span style={{ display: 'inline-block', whiteSpace: 'nowrap', fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: '999px', background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
                {s.label}
            </span>
        )
    }

    renderHowItWorks() {
        const items = [
            ['1', 'Find the order', 'Only Altitude orders that have not been invoiced yet are listed.'],
            ['2', 'Choose the right customer', 'Only customers with the same price level can be picked, so prices stay the same.'],
            ['3', 'Confirm', 'Sage, the Mayaland copy and the portal are updated for you.'],
        ]
        return (
            <ol style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
                {items.map(([n, title, text]) => (
                    <li key={n} style={{ ...CARD_STYLE, display: 'flex', gap: '0.65rem', alignItems: 'flex-start', padding: '0.8rem' }}>
                        <span style={{ flexShrink: 0, width: '1.6rem', height: '1.6rem', borderRadius: '50%', background: '#2563EB', color: '#FFFFFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700 }}>{n}</span>
                        <span>
                            <strong style={{ display: 'block', fontSize: '0.88rem', color: '#0F172A' }}>{title}</strong>
                            <span style={{ fontSize: '0.8rem', color: '#475569' }}>{text}</span>
                        </span>
                    </li>
                ))}
            </ol>
        )
    }

    // ── Render: list view ────────────────────────────────────────────────

    renderList() {
        const { orders, loadingOrders, ordersError, filterText, filterStatus, filterSalesperson } = this.state
        const filtered = this.filteredOrders()
        const visible = filtered.slice(0, LIST_MAX_SHOWN)
        const salespeople = [...new Set(orders.map((o) => o.salespersonNo).filter(Boolean))].sort()
        const countOf = (status) => status === 'all' ? orders.length : orders.filter((o) => o.status === status).length

        return (
            <div style={CARD_STYLE}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    <div>
                        <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0F172A', margin: 0 }}>Orders not invoiced yet</h2>
                        <div style={{ fontSize: '0.8rem', color: '#475569' }}>Pick the order the salesperson asked you to fix.</div>
                    </div>
                    <button style={BTN_SECONDARY} onClick={this.loadOrders} disabled={loadingOrders}>
                        <AiOutlineReload /> {loadingOrders ? 'Loading...' : 'Refresh'}
                    </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div>
                        <label htmlFor="acc-filter" style={LABEL_STYLE}>Search</label>
                        <div style={{ position: 'relative' }}>
                            <AiOutlineSearch aria-hidden="true" style={{ position: 'absolute', left: '0.7rem', top: '0.75rem', color: '#64748B' }} />
                            <input id="acc-filter" style={{ ...INPUT_STYLE, paddingLeft: '2rem' }} value={filterText} maxLength={SEARCH_MAX_CHARS}
                                placeholder="Order No., customer or salesperson"
                                onChange={(e) => this.setState({ filterText: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="acc-salesperson" style={LABEL_STYLE}>Salesperson</label>
                        <select id="acc-salesperson" style={INPUT_STYLE} value={filterSalesperson}
                            onChange={(e) => this.setState({ filterSalesperson: e.target.value })}>
                            <option value="all">All salespeople</option>
                            {salespeople.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
                        </select>
                    </div>
                </div>

                <div role="group" aria-label="Filter by status" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.9rem' }}>
                    {STATUS_FILTERS.map((s) => {
                        const active = filterStatus === s
                        return (
                            <button key={s} aria-pressed={active} onClick={() => this.setState({ filterStatus: s })}
                                style={{ border: `1px solid ${active ? '#2563EB' : '#CBD5E1'}`, background: active ? '#2563EB' : '#FFFFFF', color: active ? '#FFFFFF' : '#0F172A', borderRadius: '999px', padding: '0.3rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                                {s === 'all' ? 'All' : STATUS[s].label} ({countOf(s)})
                            </button>
                        )
                    })}
                </div>

                {ordersError && (
                    <div style={NOTICE_ERROR} role="alert"><AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} /> {ordersError}</div>
                )}

                {loadingOrders && orders.length === 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.85rem', padding: '1rem 0' }}>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Loading Altitude orders from Sage...
                    </div>
                )}

                {!loadingOrders && !ordersError && filtered.length === 0 && (
                    <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
                        {orders.length === 0 ? 'There are no open Altitude orders without an invoice.' : 'No orders match these filters.'}
                    </div>
                )}

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {visible.map((o) => (
                        <li key={o.salesOrderNo} style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '0.7rem 0.85rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.4rem 1rem', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontWeight: 700, color: '#0F172A' }}>{o.salesOrderNo}</div>
                                <div style={{ fontSize: '0.75rem', color: '#475569' }}>{formatDate(o.orderDate)} · Salesperson {o.salespersonNo || '—'}</div>
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '0.72rem', color: '#64748B' }}>Customer</div>
                                <div style={{ fontSize: '0.85rem', color: '#0F172A', overflowWrap: 'anywhere' }}>{o.customerName} <span style={{ color: '#64748B' }}>({o.customerNo})</span></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                {this.renderStatusBadge(o.status)}
                                <span style={{ fontSize: '0.8rem', color: '#334155' }}>{formatMoney(o.total)}</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <button style={BTN_PRIMARY} onClick={() => this.handleOpenOrder(o)}>Change customer</button>
                            </div>
                        </li>
                    ))}
                </ul>

                {filtered.length > LIST_MAX_SHOWN && (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: '#475569' }}>
                        Showing the first {LIST_MAX_SHOWN} of {filtered.length} orders — use the search or filters to narrow it down.
                    </div>
                )}
            </div>
        )
    }

    // ── Render: order view ───────────────────────────────────────────────

    stepIndex() {
        const { phase, selected } = this.state
        switch (phase) {
            case 'loaded': return selected ? 1 : 0
            case 'sending': return 1
            case 'verifying': case 'verifyFailed': return 2
            case 'applying': case 'applyFailed': return 3
            case 'done': return STEPS.length
            default: return 0
        }
    }

    renderStepper() {
        const index = this.stepIndex()
        const { phase } = this.state
        const failed = phase === 'verifyFailed' || phase === 'applyFailed'
        return (
            <ol aria-label="Progress" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
                {STEPS.map((label, i) => {
                    const done = i < index
                    const current = i === index
                    const bg = done ? '#16A34A' : current ? (failed ? '#DC2626' : '#2563EB') : '#E2E8F0'
                    const fg = done || current ? '#FFFFFF' : '#475569'
                    return (
                        <li key={label} aria-current={current ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', fontWeight: current ? 700 : 500, color: current ? '#0F172A' : '#475569' }}>
                            <span style={{ width: '1.4rem', height: '1.4rem', borderRadius: '50%', background: bg, color: fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700 }}>
                                {done ? '✓' : current && failed ? '!' : i + 1}
                            </span>
                            {label}
                        </li>
                    )
                })}
            </ol>
        )
    }

    renderOrderCard() {
        const { order, currentLevel, portalRecord, phase } = this.state
        const portalDiffers = portalRecord && portalRecord.customerNo !== order.customerNo

        return (
            <div style={{ padding: '0.9rem 1rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.6rem 1rem', fontSize: '0.85rem' }}>
                    <div><div style={LABEL_STYLE}>Sales Order</div><strong>{order.salesOrderNo}</strong></div>
                    <div><div style={LABEL_STYLE}>Order date</div>{formatDate(order.orderDate)}</div>
                    <div><div style={LABEL_STYLE}>Salesperson</div>{order.salespersonNo || '—'}</div>
                    <div><div style={LABEL_STYLE}>Status</div>{this.renderStatusBadge(order.status)}</div>
                    <div><div style={LABEL_STYLE}>Price level</div><strong>{currentLevel ? `Level ${currentLevel}` : '—'}</strong></div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={LABEL_STYLE}>{phase === 'done' ? 'Customer now' : 'Current customer (live in Sage)'}</div>
                        <strong style={{ overflowWrap: 'anywhere' }}>{customerLabel(order)}</strong>
                    </div>
                </div>

                {portalDiffers && phase === 'loaded' && (
                    <div style={NOTICE_WARN}>
                        <AiOutlineWarning style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                        <div>
                            The portal still has <strong>{customerLabel(portalRecord)}</strong> recorded for this order. If the customer was already changed in Sage, just update the portal.
                            <div style={{ marginTop: '0.5rem' }}>
                                <button style={BTN_SECONDARY} onClick={this.handleSyncPortalOnly}>Update portal only</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    renderCustomerSearch() {
        const { search, searching, searchError, searchOpen, results, activeIndex, selected, currentLevel, phase } = this.state
        const visible = results.slice(0, SEARCH_MAX_SHOWN)
        const editable = phase === 'loaded'
        const term = search.trim()

        return (
            <div style={{ marginTop: '1.25rem' }}>
                <label htmlFor="acc-customer-search" style={LABEL_STYLE}>Right customer (search by number or name)</label>
                <div style={{ position: 'relative', maxWidth: '520px' }}>
                    <input
                        id="acc-customer-search"
                        role="combobox"
                        aria-expanded={searchOpen}
                        aria-controls="acc-customer-options"
                        aria-autocomplete="list"
                        aria-activedescendant={searchOpen && activeIndex >= 0 ? `acc-opt-${activeIndex}` : undefined}
                        autoComplete="off"
                        style={INPUT_STYLE}
                        value={search}
                        disabled={!editable}
                        maxLength={SEARCH_MAX_CHARS}
                        placeholder="e.g. 0001234 or store name"
                        onChange={this.handleSearchChange}
                        onKeyDown={this.handleSearchKeyDown}
                        onFocus={() => visible.length && !selected && this.setState({ searchOpen: true })}
                        onBlur={() => this.setState({ searchOpen: false })}
                    />
                    {searching && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ position: 'absolute', right: '0.75rem', top: '0.7rem', color: '#64748B' }}></span>
                    )}

                    {searchOpen && (
                        <ul id="acc-customer-options" role="listbox"
                            onMouseDown={(e) => e.preventDefault()}
                            style={{ position: 'absolute', zIndex: 20, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: '300px', overflowY: 'auto', margin: 0, padding: '0.25rem 0', listStyle: 'none', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}>
                            {visible.length === 0 && (
                                <li style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#475569' }}>No customers match "{term}".</li>
                            )}
                            {visible.map((c, i) => (
                                <li key={c.customerNo} id={`acc-opt-${i}`} role="option" aria-selected={i === activeIndex} aria-disabled={c.disabled}
                                    onClick={() => this.selectCustomer(c)}
                                    onMouseEnter={() => !c.disabled && this.setState({ activeIndex: i })}
                                    style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem',
                                        padding: '0.45rem 0.75rem', fontSize: '0.85rem',
                                        cursor: c.disabled ? 'not-allowed' : 'pointer',
                                        color: c.disabled ? '#94A3B8' : '#0F172A',
                                        background: i === activeIndex && !c.disabled ? '#EFF6FF' : 'transparent',
                                    }}>
                                    <span><strong>{c.customerNo}</strong> — {c.customerName}</span>
                                    <span style={{ flexShrink: 0, fontSize: '0.72rem', fontWeight: 600, padding: '0.1rem 0.45rem', borderRadius: '999px', background: c.disabled ? '#F1F5F9' : '#DCFCE7', color: c.disabled ? '#475569' : '#166534' }}>
                                        {c.isCurrent ? 'Current' : `Level ${c.level}`}
                                    </span>
                                </li>
                            ))}
                            {results.length > SEARCH_MAX_SHOWN && (
                                <li style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', color: '#475569', borderTop: '1px solid #F1F5F9' }}>
                                    Showing the first {SEARCH_MAX_SHOWN} of {results.length} — keep typing to narrow it down.
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {term.length > 0 && term.length < SEARCH_MIN_CHARS && !selected && (
                    <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#475569' }}>Type at least {SEARCH_MIN_CHARS} characters.</div>
                )}
                {searchError && <div style={{ marginTop: '0.5rem', color: '#DC2626', fontSize: '0.85rem' }}>{searchError}</div>}

                {selected && (
                    <div style={{ ...NOTICE_INFO, background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#0F172A' }}>
                        <div style={{ lineHeight: 1.6 }}>
                            <div>From: <span style={{ textDecoration: 'line-through', color: '#64748B' }}>{customerLabel(this.state.order)}</span></div>
                            <div>To: <strong>{customerLabel(selected)}</strong></div>
                        </div>
                    </div>
                )}

                <div style={NOTICE_INFO}>
                    <AiOutlineInfoCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <span>
                        Only <strong>Level {currentLevel}</strong> customers can be picked, so the order's prices stay the same. If the customer you need is greyed out or missing, it's on another price level or was added to Sage today — ask an administrator to change it in Sage.
                    </span>
                </div>
            </div>
        )
    }

    renderFlowStatus() {
        const { phase, flowError, verifyAttempt, summary, selected, order } = this.state

        if (phase === 'sending') {
            return <div style={NOTICE_INFO} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Sending the change to Sage...</div>
        }
        if (phase === 'verifying') {
            return verifyAttempt > 1
                ? <div style={NOTICE_WARN} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Sage hasn't confirmed it yet — checking again ({verifyAttempt}/{VERIFY_DELAYS_MS.length})...</div>
                : <div style={NOTICE_INFO} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Waiting for Sage to confirm {customerLabel(selected)}...</div>
        }
        if (phase === 'verifyFailed') {
            return (
                <div style={NOTICE_ERROR} role="alert">
                    <AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <div>
                        {flowError}
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button style={BTN_PRIMARY} onClick={this.handleReloadOrder}>Reload order</button>
                            <button style={BTN_SECONDARY} onClick={() => this.handleBackToList(true)}>Back to orders</button>
                        </div>
                    </div>
                </div>
            )
        }
        if (phase === 'applying') {
            return <div style={NOTICE_INFO} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> {summary && summary.mode === 'sage' ? 'Sage confirmed the change. ' : ''}Updating the portal...</div>
        }
        if (phase === 'applyFailed') {
            return (
                <div style={NOTICE_ERROR} role="alert">
                    <AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <div>
                        {summary && summary.mode === 'sage' && <><strong>Sage was updated, but the portal is still pending.</strong> </>}
                        {flowError} Don't send the change to Sage again — just retry the portal update.
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button style={BTN_PRIMARY} onClick={this.handleRetryPortal}>Retry portal update</button>
                        </div>
                    </div>
                </div>
            )
        }
        if (phase === 'done' && summary) {
            return (
                <div style={NOTICE_OK} role="status">
                    <AiOutlineCheckCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <div>
                        <strong>{summary.mode === 'sage' ? 'Done — the customer was changed.' : 'Done — the portal now matches Sage.'}</strong>
                        <div style={{ marginTop: '0.35rem', lineHeight: 1.6 }}>
                            <div>Sales Order: <strong>{order.salesOrderNo}</strong></div>
                            {summary.from && <div>From: {customerLabel(summary.from)}</div>}
                            <div>To: <strong>{customerLabel(summary.to)}</strong></div>
                            <div style={{ fontSize: '0.75rem', color: '#15803D' }}>{summary.finishedAt} · You can let the salesperson know it's fixed.</div>
                        </div>
                        <div style={{ marginTop: '0.6rem' }}>
                            <button style={BTN_PRIMARY} onClick={() => this.handleBackToList(true)}>Back to orders</button>
                        </div>
                    </div>
                </div>
            )
        }
        if (flowError) {
            return <div style={NOTICE_ERROR} role="alert"><AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} /> {flowError}</div>
        }
        return null
    }

    renderOrderView() {
        const { phase, loadError, order, selected } = this.state
        const busy = BUSY_PHASES.includes(phase)
        const canSend = phase === 'loaded' && order && selected && !selected.disabled

        return (
            <div style={CARD_STYLE}>
                <div style={{ marginBottom: '0.9rem' }}>
                    <button style={BTN_SECONDARY} onClick={() => this.handleBackToList(phase === 'done')} disabled={busy}>
                        <AiOutlineArrowLeft /> Back to orders
                    </button>
                </div>

                {phase === 'loading' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.85rem' }}>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Checking {order ? order.salesOrderNo : 'the order'} live in Sage...
                    </div>
                )}

                {loadError && (
                    <div style={NOTICE_ERROR} role="alert"><AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} /> {loadError}</div>
                )}

                {order && phase !== 'loading' && (
                    <>
                        {this.renderStepper()}
                        {this.renderOrderCard()}
                        {phase !== 'done' && this.renderCustomerSearch()}

                        {(phase === 'loaded' || phase === 'sending') && (
                            <div style={{ marginTop: '1rem' }}>
                                <button style={canSend ? BTN_PRIMARY : BTN_DISABLED} disabled={!canSend} onClick={this.handleSend}>
                                    {phase === 'sending' ? 'Sending...' : 'Change customer'}
                                </button>
                            </div>
                        )}

                        {this.renderFlowStatus()}
                    </>
                )}
            </div>
        )
    }

    render() {
        const { phase } = this.state

        return (
            <div className="dash-page" style={{ paddingTop: '70px', background: '#F8FAFC', minHeight: '100vh' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '1.75rem 1rem' }}>
                    <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.4rem', margin: 0 }}>Customer Correction · Altitude</h1>
                    <p style={{ color: 'rgba(255,255,255,0.85)', margin: '0.25rem 0 0', fontSize: '0.85rem', maxWidth: '720px' }}>
                        Fix a Sales Order that was created with the wrong customer, before it is invoiced.
                    </p>
                </div>

                <div style={{ padding: '1.25rem 1rem', maxWidth: '980px' }}>
                    {phase === 'list' && this.renderHowItWorks()}
                    {phase === 'list' ? this.renderList() : this.renderOrderView()}

                    <p style={{ fontSize: '0.75rem', color: '#475569', fontStyle: 'italic', marginTop: '0.75rem' }}>
                        The change is confirmed by reading the order back from Sage before the portal is updated. If Sage doesn't confirm it, don't send it again right away — reload the order first, since the change may still be applied.
                    </p>
                </div>
            </div>
        )
    }
}

export default AltitudeCustomerCorrection
